import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/base/FilmCard";
import EmptyState from "@/components/base/EmptyState";
import { getFilmsByIds } from "@/lib/film";
import { topics } from "@/mocks/topics";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useDelayedLoading } from "@/hooks/useDemoLoading";
import FilmCardSkeleton from "@/components/base/FilmCardSkeleton";

export default function Topic() {
  const [params, setParams] = useSearchParams();
  const topicId = params.get("t") ?? topics[0].id;
  const topic = topics.find((item) => item.id === topicId) ?? topics[0];
  const related = topic.relatedIds
    .map((id) => topics.find((item) => item.id === id))
    .filter((item): item is (typeof topics)[number] => Boolean(item));
  const loading = useDelayedLoading(topic.id, 220);

  usePageMeta({
    title: `${topic.title} - 专题合集 - 日本一级片`,
    description: `${topic.subtitle}。${topic.intro.slice(0, 70)}`,
    keywords: `${topic.title},日本电影片单,日本影视专题,日本一级片`,
  });

  return (
    <SiteLayout>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "专题合集" }, { label: topic.title }]} />
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-[680px]">
              <p className="text-[11px] tracking-[0.24em] text-primary-600 uppercase mb-2 font-label">COLLECTION</p>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight">{topic.title}</h1>
              <p className="mt-2 text-sm md:text-base text-primary-700">{topic.subtitle}</p>
              <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-foreground-700">{topic.intro}</p>
            </div>
            <div className="shrink-0 flex items-center gap-3">
              <div className="px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-center">
                <p className="font-heading text-xl text-foreground-950">
                  {topic.groups.reduce((total, group) => total + group.filmIds.length, 0)}
                </p>
                <p className="text-xs text-foreground-500 mt-1">部影片</p>
              </div>
              <div className="px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-center">
                <p className="font-heading text-xl text-foreground-950">{topic.groups.length}</p>
                <p className="text-xs text-foreground-500 mt-1">个单元</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          {topics.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setParams(item.id === topics[0].id ? {} : { t: item.id }, { replace: true })}
              className={`h-9 px-3.5 rounded-full text-xs md:text-[13px] whitespace-nowrap transition-colors cursor-pointer border ${
                item.id === topic.id
                  ? "bg-primary-600 border-primary-600 text-background-50"
                  : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {item.title}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50/70 p-4 md:p-5 flex items-start gap-3">
          <span className="w-8 h-8 shrink-0 rounded-md bg-accent-500 text-accent-950 flex items-center justify-center">
            <i className="ri-lightbulb-line w-4 h-4 flex items-center justify-center"></i>
          </span>
          <div>
            <h2 className="text-sm text-foreground-950 font-medium">推荐理由</h2>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">{topic.reason}</p>
          </div>
        </div>

        <div className="mt-10 space-y-12">
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <div key={index}>
                  <div className="h-5 w-48 rounded skeleton-block mb-5"></div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                    {Array.from({ length: 4 }).map((__, cardIndex) => (
                      <FilmCardSkeleton key={cardIndex} />
                    ))}
                  </div>
                </div>
              ))
            : topic.groups.map((group) => {
                const groupFilms = getFilmsByIds(group.filmIds);
                return (
                  <section key={group.name}>
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <div className="flex items-center gap-3">
                        <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                        <h2 className="font-heading text-lg md:text-xl text-foreground-950">{group.name}</h2>
                      </div>
                      <span className="text-xs text-foreground-500 font-label shrink-0">{groupFilms.length} 部</span>
                    </div>
                    {groupFilms.length === 0 ? (
                      <EmptyState title="暂无相关影片" description="该单元正在整理中，可以先浏览其他单元的内容。" />
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                        {groupFilms.map((film) => (
                          <FilmCard key={film.id} film={film} />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
        </div>

        <div className="mt-14 pt-8 border-t border-background-200">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
            <h2 className="font-heading text-lg md:text-xl text-foreground-950">相关专题</h2>
            <Link
              to="/category"
              className="text-sm text-foreground-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              浏览全部影片
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/topic?t=${item.id}`}
                className="group p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm md:text-base text-foreground-950 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500 line-clamp-1">{item.subtitle}</p>
                  </div>
                  <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-600 w-4 h-4 flex items-center justify-center shrink-0"></i>
                </div>
                <p className="mt-2.5 text-xs leading-relaxed text-foreground-600 line-clamp-2">{item.reason}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}